// src/components/SpecialPricingMetrics.jsx
/** Read-only half of the Special Pricing card (CONF-106).
 *
 *  useConfigurationLogic prices the solution WITH the exec's custom hardware
 *  prices applied, so these five figures are silently the CUSTOM figures once
 *  an override exists. When `baselinePricing` is supplied (see
 *  src/utils/baselinePricing.js) each tile shows Current beside Custom; when it
 *  is null the tiles render exactly as they did before this component existed.
 *
 *  Pure presentation — no hooks, no fetching, no formatting decisions that the
 *  pricing layer should be making. Returns a FRAGMENT of grid cells because the
 *  card's CardContent is the grid; a wrapper element would collapse six cells
 *  into one. */
import React from 'react';
import { ValuePair } from './ValuePair';

const money = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// `|| 0` is carried over from the expression this replaced in App.js: a zero
// grandTotal makes the ratio NaN, and the card has always shown "0 %" for that.
const pct = (softwareTotal, grandTotal) =>
  Number((softwareTotal / grandTotal * 100) || 0)
    .toLocaleString(undefined, { maximumFractionDigits: 1 });

// A zero-capacity config makes these Infinity (see deriveCostEfficiency).
const per = (n) => (Number.isFinite(n) ? n.toFixed(2) : '—');
const perTB = (e) =>
  `$${per(e?.dollarsPerRawTB)} / $${per(e?.dollarsPerEffectiveTB)} / $${per(e?.dollarsPerCompressedEffectiveTB)}`;

const PER_TB_LABEL = 'Dollars per Raw / Useable / Effective TB';

function MetricTile({ label, current, custom }) {
  return (
    <div>
      <p className="metric-label">{label}</p>
      {custom == null
        ? <p className="metric-value">{current}</p>
        : <ValuePair current={current} custom={custom} />}
    </div>
  );
}

// The $/TB value is three numbers wide, so a Custom set beside it cannot fit
// any realistic tile width — it stacks instead, and carries the full metric
// name in both labels. The extra height it takes lands above an empty grid
// cell, so it costs the card nothing.
function PerTbTile({ current, custom }) {
  if (custom == null) {
    return (
      <div>
        <p className="metric-label">{PER_TB_LABEL}</p>
        <p className="metric-value">{current}<span>  TB</span></p>
      </div>
    );
  }
  return (
    <div>
      <p className="metric-label">{`Current ${PER_TB_LABEL}`}</p>
      <p className="metric-value">{current}<span>  TB</span></p>
      <p className="metric-label text-morganite mt-2">{`Custom ${PER_TB_LABEL}`}</p>
      <p className="metric-value text-morganite">{custom}<span>  TB</span></p>
    </div>
  );
}

const SpecialPricingMetrics = ({
  pricingData, baselinePricing, metrics, softwareSubscriptionOnly,
}) => {
  const live = pricingData?.solution ?? {};
  // No software-subscription-only special case here: computeBaselinePricing
  // already holds hardware overrides constant for those vendors, so a
  // hardware-only concession on such a quote arrives as a null baseline.
  const base = baselinePricing?.pricingData?.solution ?? null;
  const baseEfficiency = base ? (baselinePricing?.efficiency ?? null) : null;

  // With no baseline there is only one figure to show, and it is the live one —
  // so `customText` becomes the single value and `custom` stays null, which is
  // what switches every tile back to its plain single-value markup.
  const tile = (currentText, customText) => (base
    ? { current: currentText, custom: customText }
    : { current: customText, custom: null });

  const from = base ?? live;   // ignored when `base` is null — see tile()

  // CONF-258: exec metric overrides now only ever target per-node throughput
  // ratings, which never move the $/TB (capacity) denominators, so this tile
  // only ever pairs Current/Custom off a PRICING baseline, same as the rest
  // of the card.
  const perTbCurrent = baseEfficiency;

  return (
    <>
      <PerTbTile {...(perTbCurrent
        ? { current: perTB(perTbCurrent), custom: perTB(metrics) }
        : { current: perTB(metrics), custom: null })} />

      {/* Kept as an empty cell rather than dropped, so the grid does not
          reflow between subscription-only and standard quotes. */}
      {softwareSubscriptionOnly
        ? <div />
        : (
          <MetricTile
            label="VDURA Care + Install %"
            {...tile(`${pct(from.softwareTotal, from.grandTotal)} %`,
                     `${pct(live.softwareTotal, live.grandTotal)} %`)}
          />
        )}

      <MetricTile
        label="Hardware Price"
        {...tile(`$${money(softwareSubscriptionOnly ? 0 : from.hardwareTotal)}`,
                 `$${money(softwareSubscriptionOnly ? 0 : live.hardwareTotal)}`)}
      />

      {/* Spacer beneath the $/TB tile. Deliberately empty — it is what gives
          the stacked Current/Custom $/TB rows above it room to grow. */}
      <div />

      <MetricTile
        label="VDURA Total (VDURA Care + Install)"
        {...tile(`$${money(from.softwareTotal)}`, `$${money(live.softwareTotal)}`)}
      />

      <MetricTile
        label={softwareSubscriptionOnly ? 'VDURA Pricing' : 'Solution Price'}
        {...tile(`$${money(softwareSubscriptionOnly ? from.softwareTotal : from.grandTotal)}`,
                 `$${money(softwareSubscriptionOnly ? live.softwareTotal : live.grandTotal)}`)}
      />
    </>
  );
};

export default SpecialPricingMetrics;
